import { useState, useEffect, useCallback, useRef } from "react";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import "./interviste.css";
import PopUp from "../PopUp";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const Interviste = () => {
  const [loading, setLoading] = useState(false);
  const [inteviste, setInterviste] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedIntervistaId, setSelectedIntervistaId] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const downloadRef = useRef(null);

  const navigate = useNavigate();

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const fetchInterviste = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        // Transform trainerFullName filter to filter both trainer.nome and trainer.cognome with OR
        const transformedFilters = (filtersParam ?? []).map((filter) => {
          if (filter.id === "trainerFullName" && filter.value) {
            // Return the filter as-is but we'll handle it specially
            return filter;
          }
          return filter;
        }).filter(Boolean);

        // Build filter params, but handle trainerFullName specially
        const regularFilters = transformedFilters.filter(f => f.id !== "trainerFullName");
        const trainerFullNameFilter = transformedFilters.find(f => f.id === "trainerFullName");
        
        const filterParams = buildFilterQueryParams(
          regularFilters,
          filterFnsParam
        );

        // Add trainer.nome and trainer.cognome filters with OR logic for trainerFullName
        if (trainerFullNameFilter && trainerFullNameFilter.value) {
          const searchValue = trainerFullNameFilter.value.toString().trim();
          if (searchValue) {
            // Format value with % for like operator if not already formatted
            let formattedValue = searchValue;
            if (!formattedValue.includes("%") && !formattedValue.includes("*")) {
              formattedValue = `%${formattedValue}%`;
            }
            filterParams["trainer.nome_like"] = formattedValue;
            filterParams["$or_trainer.cognome_like"] = formattedValue;
          }
        }

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const response = await api.get(`/interviste`, {
          params,
        });

        const { content = [], page: pageInfo } = response.data ?? {};

        const flattened = content.map((item) => ({
          nprotocolloIntervista: item.nprotocolloIntervista || "",
          id: item.nprotocolloIntervista,
          dataIntervista: formatDate(item.dataIntervista),
          trainerFullName: `${item.trainer?.nome || ""} ${
            item.trainer?.cognome || ""
          }`.trim(),
          parlantina: item.parlantina ? "Si" : "No",
          timidezza: item.timidezza ? "Si" : "No",
          dinamicità: item.dinamicità ? "Si" : "No",
          empatia: item.empatia || "",
          inglese: item.inglese || "",
          adeguatoInsegnamento: item.adeguatoInsegnamento || "",
        }));

        setInterviste(flattened);
        setTotalRows(pageInfo?.totalElements ?? content.length);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per visualizzare le interviste!");
      navigate("/login");
      localStorage.clear();
    }
  }, [navigate]);

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") return;
    
    fetchInterviste({
      pageParam: page,
      sizeParam: pageSize,
    });
  }, [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns, fetchInterviste]);

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleRowClick = (intervista) => {
    navigate(`/intervista/${intervista.id}`);
  };

  const handleDelete = async () => {
    if (!selectedIntervistaId) {
      return;
    }
    try {
      const response = await api.delete(`/interviste/${selectedIntervistaId}`);
      if (response.status === 204) {
        toast.success("Interviste eliminate con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        fetchInterviste({
          pageParam: page,
          sizeParam: pageSize,
        });
      }
    } catch (error) {
      console.error(error);

      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setSelectedIntervistaId(null);
      setShowPopUp(false);
    }
  };

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/interviste", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `interviste.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const columns = [
    { field: "nprotocolloIntervista", header: "Nr.Protocollo", size: 100 },
    { field: "trainerFullName", header: "Docente" },
    { field: "parlantina", header: "Parlantina" },
    { field: "timidezza", header: "Timidezza" },
    { field: "dinamicità", header: "Dinamicità" },
    { field: "empatia", header: "Empatia" },
    { field: "inglese", header: "Inglese" },
    {
      header: "Azioni",
      Cell: ({ row }) => (
        <button
          className="outside-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            setShowPopUp(true);
            setSelectedIntervistaId(row.original.id);
          }}
        >
          <i className="fa-solid fa-trash"></i> Elimina
        </button>
      ),
    },
  ];

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <PopUp
        open={showPopUp}
        setOpen={setShowPopUp}
        header="Conferma Eliminazione"
        content="Sei sicuro di voler rimuovere questa intervista?"
        deleteFunction={handleDelete}
      />

      <div className="page-header">
        <div className="page-title">Interviste</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
          <div className="add-btn">
            <button onClick={() => navigate("/aggiungi-intervista")}>
              + Nuova Intervista
            </button>
          </div>
        </div>
      </div>

      <div className="page-content followup">
        <Table
          columns={columns}
          data={inteviste}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default Interviste;
